import { z } from "zod/v4";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/**
 * authoring payload が使う label entry の最小契約。
 *
 * @remarks
 * LLM 境界では record ではなく entry array を使うため、
 * compiler 側で key/label の 2 項目だけを strict に読み取る。
 */
const vizLabelEntrySchema = z
  .object({
    key: z.string().trim().min(1),
    label: z.string().trim().min(1),
  })
  .strict();

/**
 * chart label を LLM が責務として返す view で使う strict entry array。
 *
 * @remarks
 * user-facing legend/series label を compiler default へ逃がさないため、
 * chart authoring では空配列や null を許さず少なくとも 1 件を要求する。
 */
export const requiredVizLabelEntriesSchema = z
  .array(vizLabelEntrySchema)
  .min(1);

export type VizLabelEntry = z.infer<typeof vizLabelEntrySchema>;

function toLabelEntry(value: unknown): VizLabelEntry | null {
  if (!isRecord(value)) return null;
  if (typeof value.key !== "string" || typeof value.label !== "string") {
    return null;
  }

  const key = value.key.trim();
  const label = value.label.trim();
  if (key.length === 0 || label.length === 0) {
    return null;
  }

  return { key, label };
}

function upsertCanonicalPreferred<T>(args: {
  map: Record<string, T>;
  canonicalWins: Set<string>;
  canonicalKey: string;
  rawKey: string;
  value: T;
}): void {
  const isCanonical = args.rawKey === args.canonicalKey;
  if (args.canonicalKey in args.map) {
    if (!isCanonical || args.canonicalWins.has(args.canonicalKey)) return;
  }

  args.map[args.canonicalKey] = args.value;
  if (isCanonical) {
    args.canonicalWins.add(args.canonicalKey);
  }
}

/**
 * authoring entry array を canonical key ベースの label map へ正規化する。
 *
 * @remarks
 * prompt では `Array<{ key, label }>` を使って LLM readability を優先し、
 * renderer 直前でだけ `Record<string, string>` へ戻す。
 * alias や未知 key は補完せずに落とし、最後まで fail-closed を保つ。
 */
function normalizeViewLabelEntries(args: {
  entries: readonly unknown[] | null | undefined;
  allowedKeys: Set<string>;
  resolveKey: (rawKey: string) => string | null;
}): Record<string, string> {
  const normalized: Record<string, string> = {};
  const canonicalWins = new Set<string>();

  for (const entry of args.entries ?? []) {
    const parsedEntry = toLabelEntry(entry);
    if (!parsedEntry) continue;

    const resolvedKey = args.resolveKey(parsedEntry.key);
    if (!resolvedKey || !args.allowedKeys.has(resolvedKey)) continue;

    upsertCanonicalPreferred({
      map: normalized,
      canonicalWins,
      canonicalKey: resolvedKey,
      rawKey: parsedEntry.key,
      value: parsedEntry.label,
    });
  }

  return normalized;
}

/**
 * series label key を strict に実フィールドキーへ解決する。
 *
 * @remarks
 * 部分一致や接頭辞推測は行わず、存在しないキーは必ず破棄する。
 */
function resolveSeriesLabelKey(
  rawKey: string,
  allowedKeys: string[],
): string | null {
  return allowedKeys.includes(rawKey) ? rawKey : null;
}

/**
 * authoring entry array を series 用 label map へ正規化する。
 *
 * @remarks
 * chart 意図の推測を避けるため、未定義 key や alias は補完せず fail-closed で除外する。
 */
function normalizeSeriesLabelEntries(args: {
  entries: readonly VizLabelEntry[] | null | undefined;
  allowedSeriesKeys: string[];
}): Record<string, string> {
  const allowedKeySet = new Set(args.allowedSeriesKeys);
  return normalizeViewLabelEntries({
    entries: args.entries,
    allowedKeys: allowedKeySet,
    resolveKey: (rawKey) =>
      resolveSeriesLabelKey(rawKey, args.allowedSeriesKeys),
  });
}

/**
 * authoring payload から explicit な系列ラベル map を組み立てる。
 *
 * @remarks
 * chart の user-facing label は LLM が責務を持つため、
 * dataset metadata への fallback は行わず exact key match だけを採用する。
 */
export function buildSeriesLabelMap(args: {
  seriesKeys: readonly string[];
  entries: readonly VizLabelEntry[] | null | undefined;
}): Record<string, string> {
  return normalizeSeriesLabelEntries({
    entries: args.entries,
    allowedSeriesKeys: [...args.seriesKeys],
  });
}
